import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { productService } from '../../services';

// Async thunk for fetching products
export const fetchProducts = createAsyncThunk(
    'product/fetchProducts',
    async ({ page = 1, limit = 12 } = {}, { rejectWithValue }) => {
        try {
            const response = await productService.getTransformedProducts(page, limit);
            if (response.success) {
                return response;
            }
            return rejectWithValue('Failed to fetch products');
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch products');
        }
    }
);

// Async thunk for fetching single product
export const fetchProductById = createAsyncThunk(
    'product/fetchProductById',
    async (productId, { rejectWithValue }) => {
        try {
            const response = await productService.getProductById(productId);
            if (response.success) {
                return productService.transformProductData(response.data);
            }
            return rejectWithValue('Product not found');
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch product');
        }
    }
);

// Async thunk for fetching single product by slug
export const fetchProductBySlug = createAsyncThunk(
    'product/fetchProductBySlug',
    async (slug, { rejectWithValue }) => {
        try {
            const response = await productService.getProductBySlug(slug);
            if (response.success) {
                return productService.transformProductData(response.data);
            }
            return rejectWithValue('Product not found');
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch product');
        }
    }
);

// Async thunk for fetching products by category ID
export const fetchProductsByCategory = createAsyncThunk(
    'product/fetchProductsByCategory',
    async (categoryId, { rejectWithValue }) => {
        try {
            const response = await productService.getTransformedProductsByCategory(categoryId);
            if (response.success) {
                return response;
            }
            return rejectWithValue('Failed to fetch products by category');
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch products by category');
        }
    }
);

const productSlice = createSlice({
    name: 'product',
    initialState: {
        products: [],
        currentProduct: null,
        loading: false,
        error: null,
        pagination: null,
    },
    reducers: {
        setProducts(state, action) {
            state.products = action.payload;
        },
        setCurrentProduct(state, action) {
            state.currentProduct = action.payload;
        },
        clearError(state) {
            state.error = null;
        },
        clearCurrentProduct(state) {
            state.currentProduct = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch products cases
            .addCase(fetchProducts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.products = action.payload.data;
                state.pagination = action.payload.pagination;
                state.error = null;
            })
            .addCase(fetchProducts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch single product cases
            .addCase(fetchProductById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProductById.fulfilled, (state, action) => {
                state.loading = false;
                state.currentProduct = action.payload;
                state.error = null;
            })
            .addCase(fetchProductById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch single product by slug cases
            .addCase(fetchProductBySlug.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProductBySlug.fulfilled, (state, action) => {
                state.loading = false;
                state.currentProduct = action.payload;
                state.error = null;
            })
            .addCase(fetchProductBySlug.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch products by category cases
            .addCase(fetchProductsByCategory.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProductsByCategory.fulfilled, (state, action) => {
                state.loading = false;
                state.products = action.payload.data;
                state.pagination = action.payload.pagination;
                state.error = null;
            })
            .addCase(fetchProductsByCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { setProducts, setCurrentProduct, clearError, clearCurrentProduct } = productSlice.actions;
export default productSlice.reducer;
